import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Coins, Diamond, Trophy, Target, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useConfetti } from '@/hooks/useConfetti';

interface GameRewardsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isWinner: boolean;
  coinsEarned: number;
  gemsEarned: number;
  gameType: 'multiplayer' | 'bot' | 'puzzle';
  opponentName?: string;
  eloChange?: number;
  oldElo?: number;
  newElo?: number;
  isRated?: boolean;
  onRematch?: (swapColors: boolean) => void;
  showRematchButton?: boolean;
}

export function GameRewardsModal({
  open,
  onOpenChange,
  isWinner,
  coinsEarned,
  gemsEarned,
  gameType,
  opponentName,
  eloChange,
  oldElo,
  newElo,
  isRated = true,
  onRematch,
  showRematchButton = false
}: GameRewardsModalProps) {
  const [animateCoins, setAnimateCoins] = useState(false);
  const [animateGems, setAnimateGems] = useState(false);
  const [animateElo, setAnimateElo] = useState(false);
  const { fire } = useConfetti();

  useEffect(() => {
    if (open) {
      // Fire victory confetti for winners
      if (isWinner) {
        setTimeout(() => fire('victory'), 200);
      }
      
      // Stagger the animations
      setTimeout(() => setAnimateCoins(true), 300);
      setTimeout(() => setAnimateGems(true), 600);
      setTimeout(() => setAnimateElo(true), 900);
    } else {
      setAnimateCoins(false);
      setAnimateGems(false);
      setAnimateElo(false);
    }
  }, [open, isWinner, fire]);

  const getGameTypeIcon = () => {
    switch (gameType) {
      case 'puzzle':
        return <Target className="h-8 w-8 text-primary" />;
      case 'bot':
        return <Trophy className="h-8 w-8 text-primary" />;
      default:
        return <Trophy className="h-8 w-8 text-primary" />;
    }
  };

  const getTitle = () => {
    if (gameType === 'puzzle') {
      return isWinner ? 'Puzzle Solved!' : 'Puzzle Completed!';
    }
    if (coinsEarned < 0) {
      return 'Defeat';
    }
    return isWinner ? 'Victory!' : 'Good Game!';
  };

  const getSubtitle = () => {
    if (gameType === 'puzzle') {
      return 'Great problem-solving skills!';
    }
    if (coinsEarned < 0) {
      return 'Better luck next time! Practice to improve your skills.';
    }
    if (isWinner) {
      return opponentName ? `You defeated ${opponentName}!` : 'You won the game!';
    }
    return 'You played a great game!';
  };

  const getEloIcon = () => {
    if (!eloChange || eloChange === 0) return <Minus className="h-5 w-5 text-muted-foreground" />;
    if (eloChange > 0) return <TrendingUp className="h-5 w-5 text-green-500" />;
    return <TrendingDown className="h-5 w-5 text-red-500" />;
  };

  const getEloColor = () => {
    if (!eloChange || eloChange === 0) return 'text-muted-foreground';
    if (eloChange > 0) return 'text-green-600 dark:text-green-400';
    return 'text-red-600 dark:text-red-400';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="flex justify-center mb-4">
            {getGameTypeIcon()}
          </div>
          <DialogTitle className="text-2xl font-bold">
            {getTitle()}
          </DialogTitle>
          <p className="text-muted-foreground">
            {getSubtitle()}
          </p>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Elo Change - Only show for rated games */}
          {isRated && eloChange !== undefined && oldElo !== undefined && newElo !== undefined && (
            <div className={`bg-muted/50 rounded-lg p-4 transition-all duration-500 ${
              animateElo ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
            }`}>
              <div className="flex items-center justify-center gap-3">
                {getEloIcon()}
                <div className="text-center">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">{oldElo}</span>
                    <span className="text-muted-foreground">→</span>
                    <span className={`text-xl font-bold ${getEloColor()}`}>{newElo}</span>
                  </div>
                  <span className={`text-sm font-semibold ${getEloColor()}`}>
                    {eloChange > 0 ? '+' : ''}{eloChange} Rating
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Unrated Game Notice */}
          {!isRated && gameType !== 'puzzle' && (
            <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-3 text-center">
              <span className="text-sm text-blue-700 dark:text-blue-300">
                🎮 Casual Game - No rating change
              </span>
            </div>
          )}

          {/* Currency Rewards */}
          <div className="bg-muted/50 rounded-lg p-6 space-y-4">
            <h3 className="font-semibold text-center">
              {coinsEarned < 0 ? 'Currency Lost' : 'Rewards Earned'}
            </h3>
            
            <div className="flex justify-center gap-8">
              {/* Coins */}
              <div className="flex flex-col items-center">
                <div className={`flex items-center gap-2 transition-all duration-500 ${
                  animateCoins ? 'scale-110 animate-bounce' : 'scale-100'
                }`}>
                  <Coins className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                  <span className={`text-2xl font-bold ${
                    coinsEarned >= 0 
                      ? 'text-yellow-600 dark:text-yellow-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {coinsEarned >= 0 ? '+' : ''}{coinsEarned}
                  </span>
                </div>
                <span className="text-sm text-muted-foreground">Coins</span>
              </div>

              {/* Gems */}
              <div className="flex flex-col items-center">
                <div className={`flex items-center gap-2 transition-all duration-500 ${
                  animateGems ? 'scale-110 animate-bounce' : 'scale-100'
                }`}>
                  <Diamond className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    +{gemsEarned}
                  </span>
                </div>
                <span className="text-sm text-muted-foreground">Gems</span>
              </div>
            </div>

            {/* Bonus indicator for winners */}
            {isWinner && gameType !== 'puzzle' && (
              <div className="text-center">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  🎉 Victory Bonus Included!
                </span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            {showRematchButton && onRematch && (
              <div className="flex gap-2">
                <Button 
                  onClick={() => {
                    onOpenChange(false);
                    onRematch(false);
                  }} 
                  className="flex-1"
                  variant="default"
                  size="lg"
                >
                  🔁 Rematch
                </Button>
                <Button 
                  onClick={() => {
                    onOpenChange(false);
                    onRematch(true);
                  }} 
                  className="flex-1"
                  variant="outline"
                  size="lg"
                >
                  🔄 Swap Colors
                </Button>
              </div>
            )}
            <Button 
              onClick={() => onOpenChange(false)} 
              className="w-full"
              variant={showRematchButton ? "ghost" : "default"}
              size="lg"
            >
              {showRematchButton ? "Leave Game" : "Continue"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}